import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';
import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';

import { ErrorService } from '../../../../../shared/services/error-service';
import { DialogService } from '../../../../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../../../../shared/toast/toast.service';
import { LoadingService } from '../../../../../shared/services/loading.service';

import { ImportRenewalClaimsService } from '../../../../../shared/services/import-renewal-claims-service';
import { buildFilteredErrors, formatErrorValue } from '../../../../../shared/helpers/import-error-helper';

import { RenewalClaimBatchResult, RenewalClaimService, RenewalClaimTotalsResult } from '../../../../../../service/proxy';

@Component({
    standalone: true,
    selector: 'claim-data-import-data',
    templateUrl: './claims-data-import-data.component.html',
    styleUrl: './claims-data-import-data.component.scss',

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        RenewalClaimService,
        ImportRenewalClaimsService
    ]
})
export class ClaimsDataImportDataComponent implements OnInit, OnDestroy, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    @Output()
    completed = new EventEmitter<{ data: any[]; errors: any[] }>();

    public batchId: number = 0;
    public worksheetName: string = '';
    public headerRow: number = 0;
    public startRow: number = 0;
    public endRow: number = 0;

    public batch: RenewalClaimBatchResult = new RenewalClaimBatchResult();
    public totals: RenewalClaimTotalsResult = new RenewalClaimTotalsResult();

    public importInProgress: boolean = false;
    public importProgress: number = 0;
    public importCompleted: boolean = false;
    public importErrors: any[] = [];
    public showErrors = false;
    public totalErrorCount: number = 0;
    public errorRowCount: number = 0;
    public totalRows: number = 0;
    public rowsImported: number = 0;

    public sampleRows: any[] = [];
    public showData = false;
    public filteredErrors: any[] = [];
    public loading: boolean = false;
    public loadingTotals: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private renewalClaimService: RenewalClaimService,
        private importProgressService: ImportRenewalClaimsService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private dialogService: DialogService,
        private loadingService: LoadingService
    ) {}

    ngOnInit(): void {
        // Data loaded in onActivate method
    }

    ngOnDestroy(): void {
        if (this.importProgressService) {
            this.importProgressService.stopListening();
        }
    }

    processErrors(): void {
        this.filteredErrors = buildFilteredErrors(this.importErrors, 10);
    }

    formatErrorValue(value: any): string {
        return formatErrorValue(value, 120);
    }

    onActivate(): void {
        this.route.paramMap
            .subscribe(params => {
                this.batchId = Number(params.get('id'));

                if (this.batchId > 0) {
                    const mappingsChangedKey = `renewal_claim_mappings_changed_${this.batchId}`;
                    const mappingsChanged = sessionStorage.getItem(mappingsChangedKey) === 'true';

                    if (mappingsChanged) {
                        sessionStorage.removeItem(mappingsChangedKey);
                        this.deleteExistingDataAndImport();
                        return;
                    }

                    this.loadExistingBatchData();
                }
            }
        );
    }

    loadExistingBatchData(): void {
        this.loadingTotals = true;
        this.loadingService.show();
        this.cdr.detectChanges();

        this.renewalClaimService.calculateRenewalClaimImportTotals(this.batchId)
            .subscribe({
                next: (response) => {
                    this.totals = response;
                    this.loadingTotals = false;
                    this.loadingService.hide();

                    const alreadyImported = (this.totals?.totalRowsImported || 0) > 0;

                    if (alreadyImported) {
                        this.importCompleted = true;
                        this.importProgress = 100;
                        this.importInProgress = false;
                        this.cdr.detectChanges();

                        return;
                    }

                    this.deleteExistingDataAndImport();
                },
                error: (error) => {
                    this.loadingTotals = false;
                    this.loadingService.hide();
                    this.errorService.handleError(error);
                }
            });
    }

    deleteExistingDataAndImport(): void {
        this.importInProgress = true;
        this.importCompleted = false;
        this.importProgress = 0;
        this.importErrors = [];
        this.filteredErrors = [];
        this.showErrors = false;
        this.showData = false;
        this.sampleRows = [];
        this.cdr.detectChanges();

        this.renewalClaimService.deleteRenewalClaimDataByBatchId(this.batchId)
            .subscribe({
                next: () => {
                    this.startImport();
                },
                error: (error) => {
                    this.importInProgress = false;
                    this.cdr.detectChanges();
                    this.errorService.handleError(error);
                }
            });
    }

    startImport(): void {
        this.importInProgress = true;
        this.importCompleted = false;
        this.cdr.detectChanges();

        this.importProgressService.startListening(this.batchId, (entry) => {
            if (entry) {
                this.importProgress = entry.progress;
                this.importErrors = entry.errors || [];
                this.totalErrorCount = entry.totalErrorCount || entry.errors?.length || 0;
                this.errorRowCount = entry.errorRowCount || 0;
                this.totalRows = entry.totalRows || 0;
                this.rowsImported = entry.rowsImported || 0;
                this.cdr.detectChanges();
            }
            else {
                this.toastService.show('No progress data received.', 'Info');
            }

            if (this.importProgress === 100 || entry.message === 'Import completed.') {
                this.importInProgress = false;
                this.importErrors = entry.errors || [];
                this.totalErrorCount = entry.totalErrorCount || entry.errors?.length || 0;
                this.errorRowCount = entry.errorRowCount || 0;
                this.totalRows = entry.totalRows || 0;
                this.rowsImported = entry.rowsImported || 0;
                this.showErrors = this.importErrors.length > 0;
                this.processErrors();
                this.cdr.detectChanges();

                setTimeout(() => {
                    this.importInProgress = false;
                    this.importCompleted = true;

                    this.cdr.detectChanges();
                    this.getTotals();
                }, 3000);
            }
        },
        () => {
            this.importInProgress = false;

            this.cdr.detectChanges();
            this.toastService.show('Failed to start renewal claim data import.', 'Error');
        });
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            observer.next(true);
            observer.complete();
        });
    }

    onPrevious(): Observable<boolean> {
        // Reset completion flag so returning to this step will re-import
        this.importCompleted = false;
        return scheduled([true], asyncScheduler);
    }

    isValid(): boolean {
         return this.importErrors.length === 0;
    }

    getTotals(): void {
        this.loadingTotals = true;
        this.loadingService.show();
        this.cdr.detectChanges();

        this.renewalClaimService.calculateRenewalClaimImportTotals(this.batchId)
            .subscribe({
                next: (response) => {
                    this.loadingTotals = false;
                    this.loadingService.hide();
                    this.totals = response;

                    this.cdr.detectChanges();
                },
                error: (error) => {
                    this.loadingTotals = false;
                    this.loadingService.hide();
                    if (error.status === 400) {
                        this.toastService.show('Failed to fetch import batch data. Please try again.', 'Error');
                    }
                    else if (error.status === 404) {
                        this.toastService.show('Not found. Please try again.', 'Info');
                    }
                    else {
                        this.toastService.show('Failed to load batch data. Please try again.', 'Error');
                        this.errorService.handleError(error);
                    }
                }
            }
        );
    }

    formatNumber(value: number): string {
        if (!value) {
            return '0';
        }

        return value.toLocaleString('en-US');
    }

    toggleError() {
        this.showErrors = !this.showErrors;
    }

    getSampleBatchData(): void {
        this.loading = true;

        this.renewalClaimService.getRenewalClaimSampleData(this.batchId)
            .subscribe({
                next: (result) => {
                    this.sampleRows = result.sampleRows || [];

                    setTimeout(() => {
                        this.showData = true;
                        this.cdr.detectChanges();
                    });
                },
                error: (error) => {
                    if (error.status === 400) {
                        this.toastService.show('Bad request. Please try again.', 'Info');
                    }
                    else if (error.status === 404) {
                        this.toastService.show('Renewal claim batch not found.', 'Info');
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                    this.loading = false;
                },
                complete: () => {
                    this.loading = false;
                }
            }
        );
    }

    formatDate(dateString: string): string {
        if (!dateString) {
            return '';
        }

        return new Date(dateString).toLocaleDateString();
    }

    formatCurrency(amount: number): string {
        if (amount === null || amount === undefined) {
            return '';
        }

        return new Intl.NumberFormat('en-ZA').format(amount);
    }

    toggleSampleData(): void {
        if (!this.showData) {
            if (this.sampleRows.length === 0) {
                this.getSampleBatchData();
            }
            else {
                this.showData = true;
                this.cdr.detectChanges();
            }
        }
        else {
            this.showData = false;
            this.cdr.detectChanges();
        }
    }

    deleteClaimBatch(): void {
        if (!this.batchId) {
            this.toastService.show('No batch selected to delete.', 'Error');
            return;
        }
        this.dialogService.show('Delete Pending Batch', 'Are you sure you want to delete this batch?', 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.renewalClaimService.deletePendingRenewalClaimBatch(this.batchId)
                        .subscribe({
                            next: () => {
                                this.toastService.show('Renewal Claim batch deleted successfully.', 'success');
                                this.router.navigate(['/claim-data-create']);
                            },
                            error: (error: any) => {
                                this.errorService.handleError(error);
                            }
                        }
                    );
                }
            }
        );
    }
}